import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Box, LinearProgress, Paper, Table, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { OrderForm, OrderFormFilters } from 'models/order-form';
import OrderFormsPageActions from 'components/order-forms/order-forms-page-actions.component';
import OrderFormList from 'components/order-forms/order-form-list.component';
import { ApolloError, useLazyQuery } from '@apollo/client';
import InfiniteScroll from 'react-infinite-scroll-component';
import { orderFormsTemplate } from 'apollo/templates/order-form';
import { socketIo } from 'context/socket';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

const OrderForms: FC = () => {
    const { t: translate } = useTranslation(['order-forms']);
    const [filters, setFilters] = useState<OrderFormFilters>({});
    const [lastOffset, setLastOffset] = useState(0);
    const [forms, setForms] = useState<OrderForm[]>([]);
    const [loading, setLoading] = useState(true);
    const socket = useContext(socketIo.getSocketContext());

    const [orderForms, { refetch, fetchMore }] = useLazyQuery(orderFormsTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            setLastOffset(data.orderForms.length);
            setForms(data.orderForms);
            setLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        orderForms({ variables: { status: filters.status, search: filters.search, limit: 30, offset: 0 } });
    }, [filters]);

    useEffect(() => {
        socket.on('NEW_ORDER_FORM_UPLOADED', () => {
            refetch();
        });
        socket.on('ORDER_FORM_READY', () => {
            refetch();
        });
        return () => {
            socket.removeAllListeners('NEW_ORDER_FORM_UPLOADED');
            socket.removeAllListeners('ORDER_FORM_READY');
        };
    }, [socket]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, forms?.length || 0);

    return (
        <Fragment>
            <Helmet>
                <title>{translate('order-forms:ORDER_FORMS')} | Denthub</title>
            </Helmet>
            <Box mb={2}>
                <OrderFormsPageActions onFilterChange={(newFilter) => setFilters(newFilter)} />
            </Box>

            <TableContainer component={Paper} sx={{ padding: 1, marginBottom: 5 }}>
                <InfiniteScroll
                    dataLength={forms ? forms.length : 0}
                    next={handleFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 100 }}>{translate('common:STATUS')}</TableCell>
                                <TableCell sx={{ minWidth: 300 }}>{translate('common:TITLE')}</TableCell>
                                <TableCell>{translate('order-forms:SCANNED')}</TableCell>
                                <TableCell>{translate('order-forms:SCANNED_BY')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        {loading && <LinearProgress color="secondary" />}
                        <OrderFormList forms={forms} onDelete={refetch} />
                    </Table>
                </InfiniteScroll>
            </TableContainer>
        </Fragment>
    );
};

export default OrderForms;
function showBoundary(error: ApolloError) {
    throw new Error('Function not implemented.');
}
